/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Per-user preferences for what the notification ENGINE sends.
 *
 * Deliberately the same screen as Notifications → Preferences, because it is
 * the same decision: a MATRIX of notifications across channels, grouped by
 * category, over one band of global controls. Two different layouts for one
 * question would make a person learn the idea twice.
 *
 * The global row is literally shared — pausing or setting quiet hours here is
 * the same act as doing it on the live screen, written to the same document, so
 * the two can never disagree. What is separate is the per-notification half: a
 * row is keyed on the notification's id, and the live screen lists the live
 * system's masters, so an engine notification had no row anyone could write.
 *
 * A channel the notification never sends on renders as a dash, not a switch —
 * an off switch and an impossible one must not look identical.
 *
 * The browser-push control is deliberately absent. It subscribes THIS browser,
 * which is one state shared by both systems; a second switch for it would let
 * the two disagree about something neither of them owns.
 */
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import SearchInput from '../../components/common/search-input';
import { pushNotificationService } from '../../services/push-notification.service';
import { getContactId } from '../../utils/current-user';
import { browserZone, zoneOptions } from '../../utils/timezones';
import {
  notificationSettingsService,
  type NotificationSetting,
} from '../../services/notification-settings.service';
import { notificationMastersService } from '../../services/notification-masters.service';
import { notificationTypesService } from '../../services/notification-types.service';

import { CHANNEL_ICON, CHANNEL_LABEL as FULL_CHANNEL_LABEL } from '../../components/notifications/notification-format';
import { PageSkeleton } from '../../components/common/panel';

/**
 * Column order. `push` and `webPush` are distinct devices, not synonyms.
 *
 * Icons come from the shared map so a channel added there appears here too.
 * The labels are overridden because a column header has no room for
 * "Mobile push" - the shorter name is a layout decision, not a second
 * vocabulary, and every channel not named here falls through to the shared one.
 */
const CHANNEL_LABEL: Record<string, string> = {
  ...FULL_CHANNEL_LABEL,
  push: 'Mobile', webPush: 'Browser',
};

const USER_CHANNELS = ['inApp', 'email', 'sms', 'push', 'webPush', 'whatsapp'];

/**
 * A tint per channel so the column header icons read as six distinct things at
 * a glance instead of six identical grey glyphs. Purely presentational — the
 * column order and meaning still come from USER_CHANNELS.
 */
const CHANNEL_COLOR: Record<string, string> = {
  inApp: 'text-indigo-500',
  email: 'text-sky-500',
  sms: 'text-violet-500',
  push: 'text-amber-500',
  webPush: 'text-cyan-500',
  whatsapp: 'text-emerald-500',
};

/**
 * A stable accent per category so a person scanning the matrix can tell one
 * group from the next without reading every header. The palette is fixed and
 * hashed by name, so the same category always gets the same colour.
 */
const CATEGORY_ACCENTS = [
  'bg-rose-400', 'bg-amber-400', 'bg-emerald-400',
  'bg-sky-400', 'bg-violet-400', 'bg-teal-400', 'bg-fuchsia-400',
];
const categoryAccent = (name: string): string => {
  let h = 0;
  for (let i = 0; i < name.length; i += 1) h = (h * 31 + (name.codePointAt(i) ?? 0)) >>> 0;
  return CATEGORY_ACCENTS[h % CATEGORY_ACCENTS.length];
};

/**
 * The current user only needs the global band — pause, quiet hours, rate limit
 * and browser push. The per-notification matrix (and its search and legend) is
 * hidden for them: it is an admin-level detail they should not have to reason
 * about. Kept as a flag rather than deleted so it can be restored per role
 * without rebuilding the screen. All the matrix logic stays intact and simply
 * renders nothing while this is off.
 */
const SHOW_MATRIX = false;

/** One row of the matrix: an engine notification, seen as its type presents it. */
interface Row {
  id: string;
  code: string;
  description?: string | null;
  category: string;
  channels: string[];
}

const NotificationPreferences = () => {
  const authUser = useSelector((state: any) => state.auth?.user);
  const contactId = getContactId(authUser);
  const toast = useRef<Toast>(null);

  const [rows, setRows] = useState<Row[]>([]);
  const [settings, setSettings] = useState<Record<string, NotificationSetting>>({});
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');

  // Push is the one preference that is not a stored row: it belongs to this
  // browser, and only this browser can say whether it is on.
  const [pushPermission, setPushPermission] = useState<NotificationPermission>('default');
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const pushBlocked = pushPermission === 'denied';

  const keyOf = (id: string | null) => id || 'all';

  const refreshPushState = useCallback(async () => {
    setPushPermission(pushNotificationService.getPermissionStatus());
    setPushSubscribed(await pushNotificationService.checkSubscriptionStatus());
  }, []);

  useEffect(() => { refreshPushState(); }, [refreshPushState]);

  const setPush = async (on: boolean) => {
    if (!contactId) return;
    setPushBusy(true);
    try {
      if (on) await pushNotificationService.subscribe(contactId);
      else await pushNotificationService.unsubscribe(contactId);
      toast.current?.show({
        severity: on ? 'success' : 'info',
        summary: on ? 'Push enabled' : 'Push disabled',
        detail: on ? 'This browser will receive alerts'
                   : 'This browser will no longer receive alerts',
        life: 2500,
      });
    } catch (e: any) {
      toast.current?.show({
        severity: 'warn', summary: 'Push not changed',
        detail: e?.message || 'Permission denied', life: 3500,
      });
    } finally {
      await refreshPushState();
      setPushBusy(false);
    }
  };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [notifications, types, mine] = await Promise.all([
        // Each falls back to an empty page rather than failing the screen: a
        // preference list that cannot render because one lookup was slow is
        // worse than one that renders with fewer rows.
        notificationMastersService.list({ skip: 0, limit: 200 })
          .catch(() => ({ success: false, data: [] })),
        notificationTypesService.list({ skip: 0, limit: 200 })
          .catch(() => ({ success: false, data: [] })),
        notificationSettingsService.listMine().catch(() => [] as NotificationSetting[]),
      ]);

      // A notification inherits its code, category and channels from its type,
      // so the types are what make this list readable and what decide which
      // cells are switches rather than dashes.
      const byId = Object.fromEntries(((types as any).data || []).map((t: any) => [t._id, t]));
      setRows(((notifications as any).data || [])
        .filter((n: any) => n.isActive !== false)
        .map((n: any) => {
          const type = byId[String(n.notificationTypeId)];
          return {
            id: n._id,
            code: type?.notificationCode || String(n.notificationTypeId),
            description: n.description,
            category: type?.category || 'Other',
            channels: type?.channels || [],
          };
        }));

      const map: Record<string, NotificationSetting> = {};
      mine.forEach((s) => { map[keyOf(s.notificationId)] = s; });
      setSettings(map);
    } catch (e) {
      console.error('Failed to load notification preferences', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const globalSetting = settings.all;
  // The stored zone is included even when it is not one of the curated ones, so
  // a row set elsewhere shows what it actually says rather than snapping to a
  // neighbour the moment somebody opens this screen.
  const zones = useMemo(
    () => zoneOptions(globalSetting?.quietHours?.timezone),
    [globalSetting?.quietHours?.timezone],
  );
  const paused = !!globalSetting?.optedOut;

  const upsert = async (
    notificationId: string | null,
    patch: Parameters<typeof notificationSettingsService.upsert>[1],
  ) => {
    const key = keyOf(notificationId);
    try {
      const saved = await notificationSettingsService.upsert(notificationId, patch);
      setSettings((prev) => ({ ...prev, [key]: { ...prev[key], ...saved } }));
      toast.current?.show({ severity: 'success', summary: 'Saved', detail: 'Preference updated', life: 2000 });
    } catch (e) {
      console.error(e);
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Could not save preference', life: 3000 });
    }
  };

  const effectiveChannels = (row: Row): string[] => {
    const s = settings[keyOf(row.id)];
    // An explicit override — including an empty one — wins over the default.
    if (Array.isArray(s?.channels)) return s!.channels as string[];
    return row.channels;
  };

  const toggleChannel = (row: Row, channel: string, on: boolean) => {
    const current = new Set(effectiveChannels(row));
    if (on) current.add(channel); else current.delete(channel);
    upsert(row.id, { channels: Array.from(current) });
  };

  /** Group by category so the list stays navigable as notifications are added. */
  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    const hit = rows.filter((r) =>
      !q || `${r.code} ${r.description || ''}`.toLowerCase().includes(q));
    const buckets = new Map<string, Row[]>();
    hit.forEach((r) => {
      if (!buckets.has(r.category)) buckets.set(r.category, []);
      buckets.get(r.category)!.push(r);
    });
    return Array.from(buckets.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [rows, query]);

  if (loading) return <PageSkeleton panels={2} />;

  return (
    <div data-testid="SCR-NotificationPreferences">
      <Toast ref={toast} />

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
        {/* ── panel header ─────────────────────────────────────────
            With the matrix hidden the band is the whole screen, so it earns a
            title that says what these four controls are for. */}
        <div className="flex items-center gap-3 border-b border-gray-100 bg-gradient-to-r from-indigo-50/60 to-transparent px-5 py-4 dark:border-gray-700 dark:from-indigo-950/30">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-500 text-white shadow-sm shadow-indigo-500/30">
            <i className="pi pi-bell text-sm" />
          </span>
          <div>
            <div className="text-sm font-semibold text-ink">Notification settings</div>
            <div className="text-xs text-ink-muted">Choose when and how alerts reach you.</div>
          </div>
        </div>

        {/* ── settings list ────────────────────────────────────────
            SaaS settings pattern: one full-width row per preference — a tinted
            icon and a title/description on the left, the control on the right.
            Four unrelated settings now read as a list a person scans top to
            bottom, not a toolbar they decode left to right. Every control and
            its binding is exactly the one from the old band. */}
        <div className="divide-y divide-gray-100 dark:divide-gray-700/60">

          {/* Pause everything */}
          <div className="flex flex-wrap items-center justify-between gap-4 px-5 py-4">
            <div className="flex items-start gap-3">
              <span className="mt-px flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-50 text-amber-500 dark:bg-amber-950/40 dark:text-amber-400">
                <i className="pi pi-pause text-sm" />
              </span>
              <div>
                <div className="text-sm font-semibold text-ink">Pause everything</div>
                <div className="mt-0.5 text-xs text-ink-muted">
                  Silence every channel at once — nothing is delivered until you resume.
                </div>
              </div>
            </div>
            <InputSwitch
              checked={paused}
              onChange={(e) => upsert(null, { optedOut: e.value })}
              aria-label="Pause everything"
              data-testid="NotificationPreferences.Pause"
            />
          </div>

          {/* Quiet hours */}
          <div className="flex flex-wrap items-center justify-between gap-4 px-5 py-4">
            <div className="flex items-start gap-3">
              <span className="mt-px flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-500 dark:bg-indigo-950/50 dark:text-indigo-400">
                <i className="pi pi-moon text-sm" />
              </span>
              <div>
                <div className="text-sm font-semibold text-ink">Quiet hours</div>
                <div className="mt-0.5 text-xs text-ink-muted">
                  Hold alerts between these hours, in the timezone you choose.
                </div>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {/* Both ends of the window live in one pill so start and end read
                  as a single range, not two loose fields. type="time" so a
                  half-typed value can never reach the API. */}
              <div className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 shadow-sm dark:border-gray-600 dark:bg-gray-900/50">
                <i className="pi pi-clock text-xs text-gray-400" />
                <InputText
                  type="time"
                  value={globalSetting?.quietHours?.start || ''}
                  className="w-[92px] !border-0 !bg-transparent !p-0 text-center font-mono text-base tabular-nums text-gray-900 !shadow-none focus:!shadow-none dark:text-white"
                  aria-label="Quiet hours start"
                  data-testid="NotificationPreferences.QuietStart"
                  onChange={(e) =>
                    upsert(null, { quietHours: { ...globalSetting?.quietHours, start: e.target.value } })}
                />
                <span className="text-gray-300 dark:text-gray-600">→</span>
                <InputText
                  type="time"
                  value={globalSetting?.quietHours?.end || ''}
                  className="w-[92px] !border-0 !bg-transparent !p-0 text-center font-mono text-base tabular-nums text-gray-900 !shadow-none focus:!shadow-none dark:text-white"
                  aria-label="Quiet hours end"
                  data-testid="NotificationPreferences.QuietEnd"
                  onChange={(e) =>
                    upsert(null, { quietHours: { ...globalSetting?.quietHours, end: e.target.value } })}
                />
              </div>
              {/* The hours are compared in THIS zone, so it cannot be implicit:
                  without it, hours set here were read in the deployment's and
                  somebody in another zone was silenced at the wrong time of day. */}
              <Dropdown
                value={globalSetting?.quietHours?.timezone || browserZone()}
                options={zones}
                optionLabel="label"
                optionValue="value"
                filter
                className="p-inputtext-sm w-[180px]"
                aria-label="Quiet hours timezone"
                data-testid="NotificationPreferences.QuietZone"
                onChange={(e) =>
                  upsert(null, { quietHours: { ...globalSetting?.quietHours, timezone: e.value } })}
              />
            </div>
          </div>

          {/* Rate limit — honoured by the engine since it counts delivered
              fires per person; settable nowhere until now. */}
          <div className="flex flex-wrap items-center justify-between gap-4 px-5 py-4">
            <div className="flex items-start gap-3">
              <span className="mt-px flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-violet-50 text-violet-500 dark:bg-violet-950/40 dark:text-violet-400">
                <i className="pi pi-replay text-sm" />
              </span>
              <div>
                <div className="text-sm font-semibold text-ink">Rate limit</div>
                <div className="mt-0.5 text-xs text-ink-muted">
                  Cap how often the same alert can reach you.
                </div>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 text-xs text-ink-muted">
              <span>At most</span>
              <InputNumber
                value={globalSetting?.maxRepeats ?? null}
                min={1} max={99} showButtons={false}
                inputClassName="p-inputtext-sm w-[58px] text-center tabular-nums"
                placeholder="any"
                aria-label="Maximum repeats"
                data-testid="NotificationPreferences.MaxRepeats"
                onValueChange={(e) => upsert(null, { maxRepeats: e.value ?? null })}
              />
              <span>times, then wait</span>
              <InputNumber
                value={globalSetting?.repeatCooldownMinutes ?? null}
                min={1} max={10080} showButtons={false}
                inputClassName="p-inputtext-sm w-[64px] text-center tabular-nums"
                placeholder="—"
                aria-label="Repeat cooldown in minutes"
                data-testid="NotificationPreferences.RepeatCooldown"
                onValueChange={(e) => upsert(null, { repeatCooldownMinutes: e.value ?? null })}
              />
              <span>min</span>
            </div>
          </div>

          {/* This browser — the one setting that decides whether anything
              arrives once the tab is shut. */}
          <div className="flex flex-wrap items-center justify-between gap-4 px-5 py-4">
            <div className="flex items-start gap-3">
              <span className="mt-px flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-cyan-50 text-cyan-500 dark:bg-cyan-950/40 dark:text-cyan-400">
                <i className="pi pi-desktop text-sm" />
              </span>
              <div>
                <div className="text-sm font-semibold text-ink">This browser</div>
                <div className="mt-0.5 text-xs text-ink-muted">
                  Receive push alerts here even when the tab is closed.
                </div>
              </div>
            </div>
            {pushBlocked ? (
              // A browser-level block is the one state a switch cannot express:
              // flipping it would do nothing and say nothing.
              <span className="rounded-md bg-rose-50 px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-wider text-rose-700 dark:bg-rose-950 dark:text-rose-300">
                Blocked in browser
              </span>
            ) : (
              <div className="flex items-center gap-2.5">
                <span className={`text-xs font-semibold ${
                  pushSubscribed
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : 'text-ink-muted'}`}>
                  {pushSubscribed ? 'On' : 'Off'}
                </span>
                <InputSwitch
                  checked={pushSubscribed}
                  disabled={pushBusy || !contactId}
                  onChange={(e) => setPush(!!e.value)}
                  aria-label="Push notifications in this browser"
                  data-testid="NotificationPreferences.Push"
                />
              </div>
            )}
          </div>

          {SHOW_MATRIX && (
            <div className="px-5 py-3">
              <SearchInput
                value={query}
                onChange={setQuery}
                placeholder={`Search ${rows.length} notifications...`}
                width={220}
                testId="NotificationPreferences.Filter.Search"
              />
            </div>
          )}
        </div>

        {paused && (
          <div className="flex items-center gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs font-medium text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-200">
            <i className="pi pi-pause-circle text-sm" />
            <span>Everything is paused — nothing is delivered on any channel until you resume.</span>
          </div>
        )}

        {/* ── matrix (hidden for the current user; see SHOW_MATRIX) ── */}
        {SHOW_MATRIX && (
        <>
        <div className="max-h-[520px] overflow-auto">
          <table className="w-full min-w-[680px] border-collapse">
            <thead>
              <tr>
                <th className="sticky top-0 z-10 border-b border-gray-200 bg-white px-4 py-2.5 text-left font-mono text-[9.5px] font-bold uppercase tracking-wider text-gray-400 dark:border-gray-700 dark:bg-gray-800">
                  Notification
                </th>
                {USER_CHANNELS.map((c) => (
                  <th key={c} className="sticky top-0 z-10 border-b border-gray-200 bg-white px-2 py-2.5 text-center font-mono text-[9.5px] font-bold uppercase tracking-wider text-gray-400 dark:border-gray-700 dark:bg-gray-800">
                    <span className="flex flex-col items-center gap-1">
                      <i className={`pi ${CHANNEL_ICON[c]} text-[12px] ${CHANNEL_COLOR[c] || ''}`} />
                      {CHANNEL_LABEL[c]}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grouped.length === 0 && (
                <tr>
                  <td colSpan={USER_CHANNELS.length + 1} className="py-16 text-center text-sm text-gray-400">
                    {query ? `No notification matches “${query}”` : 'No notifications configured yet.'}
                  </td>
                </tr>
              )}

              {grouped.map(([cat, catRows]) => (
                // Keyed Fragment: a category emits a header row plus its rows,
                // and the shorthand <> cannot carry the key React needs here.
                <Fragment key={cat}>
                  <tr>
                    <td colSpan={USER_CHANNELS.length + 1}
                        className="border-b border-gray-100 bg-gray-50 px-4 py-1.5 font-mono text-[9.5px] font-bold uppercase tracking-[0.14em] text-gray-500 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-300">
                      <span className="flex items-center gap-2">
                        {/* A colour tag makes each group findable at a glance;
                            it repeats the category, so it adds no new meaning. */}
                        <span className={`inline-block h-3 w-1 rounded-full ${categoryAccent(cat)}`} />
                        {cat} · {catRows.length} notification{catRows.length > 1 ? 's' : ''}
                      </span>
                    </td>
                  </tr>

                  {catRows.map((row, i) => {
                    const available = row.channels;
                    const on = new Set(effectiveChannels(row));
                    return (
                      <tr key={row.id} className={`transition-colors hover:bg-indigo-50/50 dark:hover:bg-gray-700/30 ${
                        i % 2 ? 'bg-gray-50/40 dark:bg-gray-900/20' : ''}`}>
                        <td className="border-b border-gray-100 px-4 py-2 dark:border-gray-700">
                          {/* The code comes from the type — a notification has no
                              name of its own — so the description carries the
                              plain English underneath when there is one. */}
                          <div className="font-mono text-[12px] font-medium text-ink">
                            {row.code}
                          </div>
                          {row.description && (
                            <div className="truncate text-[11.5px] text-ink-muted">
                              {row.description}
                            </div>
                          )}
                        </td>
                        {USER_CHANNELS.map((c) => (
                          <td key={c} className="border-b border-gray-100 px-2 py-2 text-center dark:border-gray-700">
                            {available.includes(c) ? (
                              <InputSwitch
                                checked={on.has(c) && !paused}
                                disabled={paused}
                                onChange={(e) => toggleChannel(row, c, e.value)}
                                aria-label={`${CHANNEL_LABEL[c]} for ${row.code}`}
                                data-testid={`NotificationPreferences.Channel.${row.code}.${c}`}
                              />
                            ) : (
                              <span
                                className="inline-block h-px w-2.5 bg-gray-300 align-middle dark:bg-gray-600"
                                title={`This notification never sends on ${CHANNEL_LABEL[c]}`}
                              />
                            )}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap gap-5 border-t border-gray-200 px-4 py-2.5 text-[11.5px] text-gray-500 dark:border-gray-700 dark:text-gray-400">
          <span>Switch on — you receive it</span>
          <span>Switch off — you silenced it</span>
          <span className="flex items-center gap-2">
            <span className="inline-block h-px w-2.5 bg-gray-300 dark:bg-gray-600" />
            <span>This notification never sends on that channel</span>
          </span>
        </div>
        </>
        )}
      </div>
    </div>
  );
};

export default NotificationPreferences;
